import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  FormHelperText,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {
  Add as AddIcon,
  Event as EventIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  CalendarToday as CalendarTodayIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Science as ScienceIcon,
  FilterList as FilterListIcon,
  ArrowForward as ArrowForwardIcon,
  People as PeopleIcon,
  Room as RoomIcon,
} from "@mui/icons-material";
import Layout from "../common/Layout";
import { labAPI, inventoryAPI, bookingAPI, userAPI } from "../../utils/api";
import { useAuth } from "../../context/AuthContext";
// Import the booking log helpers
import {
  getUserNameFromLog,
  getResourceNameFromLog,
  formatActivityType,
} from "../../utils/bookingLogHelper";

const BookingSystem = () => {
  // Get Firebase auth user and Firestore profile
  const { currentUser, userProfile } = useAuth();
  // Use Firestore userProfile for role/department checks
  const role = userProfile?.role;
  const department = userProfile?.department;
  const isAdmin = role === "admin";
  const isFaculty = role === "faculty";
  const isScholar = role === "scholar";
  const isStudent = role === "student";
  const showNewBookingTab = isAdmin || isFaculty || isScholar;

  // State for data
  const [labs, setLabs] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [pendingBookings, setPendingBookings] = useState([]);
  const [loading, setLoading] = useState({
    labs: true,
    equipment: true,
    bookings: true,
    pendingBookings: true,
  });
  const [error, setError] = useState(null);

  // UI state
  // Calculate tab indices dynamically based on role
  const tabList = [
    { label: "My Bookings", show: true },
    { label: "New Booking", show: showNewBookingTab },
    { label: "Approve Bookings", show: isAdmin || isFaculty },
    { label: "Booking Logbook", show: true }, // New logbook tab, visible to all
  ];
  // Use React.useMemo to prevent recreating the visibleTabs array on each render
  const visibleTabs = React.useMemo(() => {
    return tabList.filter((tab) => tab.show);
  }, [showNewBookingTab, isAdmin, isFaculty]); // Only recreate when these values change

  const [activeTab, setActiveTab] = useState(0); // index in visibleTabs
  const [bookingType, setBookingType] = useState("lab_session"); // 'lab_session' or 'equipment'
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    status: "all", // 'all', 'pending', 'approved', 'rejected'
    dateRange: {
      start: null,
      end: null,
    },
    activityType: "all", // 'all', 'created', 'approved', 'rejected', 'cancelled'
  });

  // State for booking logs
  const [bookingLogs, setBookingLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  // State for users (needed for name resolution in logs)
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Dialog states
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    resourceType: "lab_session",
    resourceId: "",
    startTime: dayjs().add(1, "hour").startOf("hour"),
    endTime: dayjs().add(2, "hour").startOf("hour"),
    purpose: "",
    notes: "",
  });

  // Approval form state
  const [approvalData, setApprovalData] = useState({
    status: "approved",
    notes: "",
  });

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  // Undo support state
  const [lastAction, setLastAction] = useState(null);
  const [undoTimer, setUndoTimer] = useState(null);

  // Fetch data on component mount
  useEffect(() => {
    fetchLabs();
    fetchEquipment();
    fetchBookings();

    // If admin or faculty, fetch pending bookings
    if (isAdmin || isFaculty) {
      fetchPendingBookings();
    }
  }, [isAdmin, isFaculty]);

  // Fetch labs data
  const fetchLabs = async () => {
    setLoading((prev) => ({ ...prev, labs: true }));
    try {
      const response = await labAPI.getAll();
      // Map Firestore `id` to `_id` and inject department name
      const labsData = response.data.map((lab) => ({
        ...lab,
        _id: lab.id || lab._id,
        department: lab.department || userProfile.department,
      }));
      setLabs(labsData);
    } catch (err) {
      console.error("Error fetching labs:", err);
      // Mock data for development
      setLabs([
        {
          _id: "lab1",
          name: "Computer Science Lab A",
          department: "Computer Science",
          location: {
            building: "Building A",
            floor: "1st Floor",
            room: "101",
          },
          capacity: 30,
        },
        {
          _id: "lab2",
          name: "Physics Laboratory",
          department: "Physics",
          location: {
            building: "Science Building",
            floor: "2nd Floor",
            room: "205",
          },
          capacity: 25,
        },
      ]);
    } finally {
      setLoading((prev) => ({ ...prev, labs: false }));
    }
  };

  // Fetch equipment data
  const fetchEquipment = async () => {
    setLoading((prev) => ({ ...prev, equipment: true }));
    try {
      const response = await inventoryAPI.getAll();
      // Filter only bookable items
      const bookableItems = response.data.filter((item) => item.isBookable);
      setEquipment(bookableItems);
    } catch (err) {
      console.error("Error fetching equipment:", err);
      // Mock data for development
      setEquipment([
        {
          _id: "eq1",
          name: "Dell XPS Workstation",
          category: "computers",
          isBookable: true,
          quantity: 15,
        },
        {
          _id: "eq2",
          name: "NVIDIA GTX 3080",
          category: "hardware",
          isBookable: true,
          quantity: 8,
        },
        {
          _id: "eq3",
          name: "Oscilloscope",
          category: "instruments",
          isBookable: true,
          quantity: 12,
        },
        {
          _id: "eq4",
          name: "Wave Generator",
          category: "instruments",
          isBookable: true,
          quantity: 6,
        },
      ]);
    } finally {
      setLoading((prev) => ({ ...prev, equipment: false }));
    }
  };

  // Helper extract timestamp from createdAt
  const getCreatedAtTime = (createdAt) => {
    if (!createdAt) return 0;
    if (typeof createdAt === "object") {
      if (createdAt.seconds !== undefined) return createdAt.seconds * 1000;
      if (createdAt._seconds !== undefined) return createdAt._seconds * 1000;
      if (typeof createdAt.toDate === "function")
        return createdAt.toDate().getTime();
    }
    const dt = new Date(createdAt);
    if (!isNaN(dt.getTime())) return dt.getTime();
    const num = parseInt(createdAt);
    return isNaN(num) ? 0 : num;
  };

  // Fetch user's bookings
  const fetchBookings = async () => {
    setLoading((prev) => ({ ...prev, bookings: true }));
    try {
      console.log("[BOOKING] Fetching user bookings from API");
      const response = await bookingAPI.getAll();
      console.log("[BOOKING] Received bookings:", response.data);

      // Sort bookings by createdAt descending (most recent first)
      const sorted = [...response.data].sort((a, b) => {
        const aTime = getCreatedAtTime(a.createdAt);
        const bTime = getCreatedAtTime(b.createdAt);
        return bTime - aTime;
      });

      setBookings(sorted);
      setLoading((prev) => ({ ...prev, bookings: false }));
    } catch (err) {
      console.error("[BOOKING] Error fetching bookings:", err);
      setBookings([]);
      setLoading((prev) => ({ ...prev, bookings: false }));
    }
  };

  // Fetch pending bookings (admin/faculty only)
  const fetchPendingBookings = async () => {
    setLoading((prev) => ({ ...prev, pendingBookings: true }));
    try {
      console.log("[BOOKING] Fetching pending bookings from API");
      const response = await bookingAPI.getPending();
      console.log("[BOOKING] Received pending bookings:", response.data);

      // Sort pending bookings by createdAt descending (most recent first)
      const sorted = [...response.data].sort((a, b) => {
        const aTime = getCreatedAtTime(a.createdAt);
        const bTime = getCreatedAtTime(b.createdAt);
        return bTime - aTime;
      });

      setPendingBookings(sorted);
      setLoading((prev) => ({ ...prev, pendingBookings: false }));
    } catch (err) {
      console.error("[BOOKING] Error fetching pending bookings:", err);
      setPendingBookings([]);
      setLoading((prev) => ({ ...prev, pendingBookings: false }));
    }
  };

  // Fetch booking logs for the logbook tab
  const fetchBookingLogs = async () => {
    setLoadingLogs(true);
    try {
      console.log("[BOOKING] Fetching booking logs");
      // Extract any filter parameters
      const params = {};
      if (filters.dateRange.start) params.startDate = filters.dateRange.start;
      if (filters.dateRange.end) params.endDate = filters.dateRange.end;
      if (filters.activityType !== "all")
        params.activityType = filters.activityType;
      if (search) params.search = search;

      const response = await bookingAPI.getLogs(params);
      console.log("[BOOKING] Received booking logs:", response.data);
      // Sort logs by timestamp/createdAt descending (most recent first)
      const sorted = [...response.data].sort(
        (a, b) =>
          new Date(b.timestamp || b.createdAt) -
          new Date(a.timestamp || a.createdAt)
      );
      setBookingLogs(sorted);
    } catch (err) {
      console.error("[BOOKING] Error fetching booking logs:", err);
      setSnackbar({
        open: true,
        message: "Failed to load booking logs. Please try again.",
        severity: "error",
      });
      // If API call fails, use empty array to avoid UI errors
      setBookingLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  };

  // Fetch users for the logbook when viewing that tab
  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      console.log("[BOOKING] Fetching users for name resolution in logs");
      const response = await userAPI.getAll();
      console.log("[BOOKING] Received users:", response.data);
      setUsers(response.data);
    } catch (err) {
      console.error("[BOOKING] Error fetching users:", err);
      // If API call fails, use empty array
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Load users and booking logs when the logbook tab is selected
  useEffect(() => {
    const isLogbookTab =
      activeTab === visibleTabs.findIndex((t) => t.label === "Booking Logbook");
    if (isLogbookTab) {
      fetchBookingLogs();
      fetchUsers(); // Fetch users for name resolution
    }
  }, [activeTab]); // Only depend on activeTab, not visibleTabs

  // Helper: check if logbook tab is active
  const isLogbookTabActive = () =>
    activeTab === visibleTabs.findIndex((t) => t.label === "Booking Logbook");

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Handle booking type change (lab session or equipment)
  const handleBookingTypeChange = (event) => {
    setBookingType(event.target.value);
    setFormData({
      ...formData,
      resourceType: event.target.value,
      resourceId: "",
    });
  };

  // Handle search input
  const handleSearchChange = (e) => {
    setSearch(e.target.value.toLowerCase());
  };

  // Handle filter changes
  const handleFilterChange = (filter, value) => {
    setFilters({
      ...filters,
      [filter]: value,
    });
  };

  // Open dialog to create new booking
  const handleNewBooking = () => {
    setFormData({
      resourceType: bookingType,
      resourceId: "",
      startTime: dayjs().add(1, "hour").startOf("hour"),
      endTime: dayjs().add(2, "hour").startOf("hour"),
      purpose: "",
      notes: "",
    });
    setBookingDialogOpen(true);
  };

  // Open dialog to approve/reject booking
  const handleApproveBookingDialog = (booking) => {
    setSelectedBooking(booking);
    setApprovalData({
      status: "approved",
      notes: "",
    });
    setApproveDialogOpen(true);
  };

  // Open confirmation dialog to cancel/delete booking
  const handleCancelBookingDialog = (booking) => {
    setSelectedBooking(booking);
    setDeleteConfirmOpen(true);
  };

  // Close dialogs
  const handleCloseDialog = () => {
    setBookingDialogOpen(false);
    setApproveDialogOpen(false);
    setDeleteConfirmOpen(false);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Handle date-time changes
  const handleDateTimeChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Handle approval form input changes
  const handleApprovalInputChange = (e) => {
    const { name, value } = e.target;
    setApprovalData({
      ...approvalData,
      [name]: value,
    });
  };

  // Handle undo of booking actions
  const handleUndo = async () => {
    if (!lastAction) return;
    clearTimeout(undoTimer);
    const { type, bookingId } = lastAction;
    if (type === "create") {
      try {
        await bookingAPI.updateStatus(bookingId, { status: "cancelled" });
        setSnackbar({
          open: true,
          message: "Booking creation undone",
          severity: "info",
        });
      } catch (err) {
        console.error("Undo create failed", err);
      }
    }
    setLastAction(null);
    fetchBookings(); // Refresh main bookings
    if (isAdmin || isFaculty) fetchPendingBookings();
    if (isLogbookTabActive()) fetchBookingLogs();
  };

  // Submit form to create booking
  const handleSubmitBooking = async (submissionData) => {
    // Debug the incoming data from the child component
    console.log("🔍 handleSubmitBooking received data:", submissionData);
    console.log("🔍 Parent formData state before update:", formData);

    // Update parent formData with the child component's data
    setFormData(submissionData);

    // Validate form using the provided submission data
    if (!submissionData.resourceId || !submissionData.purpose) {
      console.error("❌ VALIDATION FAILED - Missing required fields:", {
        resourceId: submissionData.resourceId,
        purpose: submissionData.purpose,
      });
      setSnackbar({
        open: true,
        message: "Please fill all required fields",
        severity: "error",
      });
      return;
    }

    // Validate dates
    if (!submissionData.startTime || !submissionData.endTime) {
      console.error("❌ VALIDATION FAILED - Missing date/time fields:", {
        startTime: submissionData.startTime,
        endTime: submissionData.endTime,
      });
      setSnackbar({
        open: true,
        message: "Please select start and end times",
        severity: "error",
      });
      return;
    }

    // Validate end time is after start time
    if (submissionData.endTime.isBefore(submissionData.startTime)) {
      console.error("❌ VALIDATION FAILED - End time is before start time:", {
        startTime: submissionData.startTime.format(),
        endTime: submissionData.endTime.format(),
      });
      setSnackbar({
        open: true,
        message: "End time must be after start time",
        severity: "error",
      });
      return;
    }

    console.log("✅ Form validation passed, proceeding with booking creation");

    try {
      // Convert dayjs to Date or ISO string for API
      const bookingData = {
        ...submissionData,
        startTime: submissionData.startTime.toISOString(),
        endTime: submissionData.endTime.toISOString(),
        // Associate with current auth user and department
        userId: currentUser.uid,
        department: userProfile.department || "",
        status: isAdmin || isFaculty ? "approved" : "pending",
      };

      console.log("📦 Final booking data being sent to API:", bookingData);
      const response = await bookingAPI.create(bookingData);
      const created = response.data;
      // Show snackbar with undo option
      setSnackbar({
        open: true,
        message: "Booking created",
        severity: "success",
      });
      // Track create action for undo
      setLastAction({ type: "create", bookingId: created._id || created.id });
      // Clear lastAction after 6 seconds
      const timer = setTimeout(() => setLastAction(null), 6000);
      setUndoTimer(timer);
      handleCloseDialog();
      fetchBookings();
      // Logs refresh
      if (isLogbookTabActive()) fetchBookingLogs();
    } catch (err) {
      console.error("❌ Error creating booking:", err);

      // Check for conflict error message in the response
      if (err.response && err.response.data) {
        const conflictData = err.response.data;

        if (
          conflictData.message &&
          conflictData.message.includes("already booked")
        ) {
          // Show user-friendly conflict message with details about the conflicting booking
          let conflictMessage =
            "This resource is already booked during the selected time.";

          if (conflictData.conflict) {
            const conflict = conflictData.conflict;
            const resourceName = conflict.resourceId?.name || "Resource";
            const startTime = formatDateTime(conflict.startTime);
            const endTime = formatDateTime(conflict.endTime);

            conflictMessage = `${resourceName} is already booked from ${startTime} to ${endTime}. Please select a different time.`;
          }

          setSnackbar({
            open: true,
            message: conflictMessage,
            severity: "warning",
          });
        } else {
          // Generic error message
          setSnackbar({
            open: true,
            message:
              conflictData.message ||
              "Failed to create booking. Please try again.",
            severity: "error",
          });
        }
      } else {
        // Generic error if we can't parse the response
        setSnackbar({
          open: true,
          message: "Failed to create booking. Please try again.",
          severity: "error",
        });
      }
    }
  };

  // Submit approval/rejection for a booking
  const handleSubmitApproval = async () => {
    try {
      await bookingAPI.updateStatus(selectedBooking._id, approvalData);

      setSnackbar({
        open: true,
        message: `Booking ${
          approvalData.status === "approved" ? "approved" : "rejected"
        } successfully`,
        severity: "success",
      });

      handleCloseDialog();
      fetchPendingBookings(); // Refresh pending bookings list
      fetchBookings(); // Refresh all bookings list
      // Also refresh logs if logbook tab is active
      if (isLogbookTabActive()) fetchBookingLogs();
    } catch (err) {
      console.error("Error updating booking status:", err);
      setSnackbar({
        open: true,
        message: "Failed to update booking status. Please try again.",
        severity: "error",
      });

      // For development: simulate successful approval
      handleCloseDialog();
      fetchPendingBookings(); // Refresh pending bookings list
      fetchBookings(); // Refresh all bookings list
      if (isLogbookTabActive()) fetchBookingLogs();
    }
  };

  // Cancel/delete a booking
  const handleCancelBooking = async () => {
    try {
      // Get the booking ID, handling both possible field names (id or _id)
      const bookingId = selectedBooking.id || selectedBooking._id;

      console.log("[BOOKING] Cancelling booking with ID:", bookingId);

      if (!bookingId) {
        console.error(
          "[BOOKING] Unable to determine booking ID",
          selectedBooking
        );
        setSnackbar({
          open: true,
          message: "Could not identify booking to cancel. Please try again.",
          severity: "error",
        });
        setDeleteConfirmOpen(false);
        return;
      }

      // Cancel the booking with the correct ID
      await bookingAPI.updateStatus(bookingId, {
        status: "cancelled",
      });

      setSnackbar({
        open: true,
        message: "Booking cancelled",
        severity: "info",
      });
      setDeleteConfirmOpen(false);
      fetchBookings(); // Refresh bookings list
      // Also refresh logs if logbook tab is active
      if (isLogbookTabActive()) fetchBookingLogs();
    } catch (err) {
      console.error("[BOOKING] Error cancelling booking:", err);

      // Show more specific error message if available
      const errorMessage =
        err.response?.data?.message ||
        "Failed to cancel booking. Please try again.";

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });

      setDeleteConfirmOpen(false);
    }
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Format datetime for display
  const formatDateTime = (dateString) => {
    try {
      // Enhanced debug log with more context
      console.log(
        "Formatting date value:",
        dateString,
        "Type:",
        typeof dateString,
        "Is object:",
        typeof dateString === "object"
          ? JSON.stringify(dateString)
          : "not object"
      );

      // Handle Firestore Timestamp objects
      if (dateString && typeof dateString === "object") {
        // Case 1: Firestore Timestamp with seconds and nanoseconds
        if (
          dateString.seconds !== undefined &&
          dateString.nanoseconds !== undefined
        ) {
          console.log("Processing Firestore Timestamp format");
          return new Date(dateString.seconds * 1000).toLocaleString();
        }

        // Case 2: Firebase serialized date with toDate method
        if (typeof dateString.toDate === "function") {
          console.log("Processing Firebase date with toDate");
          return dateString.toDate().toLocaleString();
        }

        // Case 3: Standard JavaScript Date object
        if (dateString instanceof Date) {
          console.log("Processing standard Date object");
          return dateString.toLocaleString();
        }

        // Case 4: Object with _seconds and _nanoseconds (sometimes returned by Firestore)
        if (dateString._seconds !== undefined) {
          console.log("Processing Firestore _seconds format");
          return new Date(dateString._seconds * 1000).toLocaleString();
        }

        // If we get an object that's not a recognized date format, show its structure
        console.error(
          "Unhandled date object format:",
          JSON.stringify(dateString)
        );

        // Attempt to convert stringify'd object to date as a last resort
        try {
          return new Date(JSON.stringify(dateString)).toLocaleString();
        } catch (innerError) {
          return "Unknown date format";
        }
      }

      // Handle string date formats
      if (typeof dateString === "string") {
        console.log("Processing string date:", dateString);

        // Try to parse as ISO string
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          return date.toLocaleString();
        }

        // If not a valid ISO string, check if it's a numeric timestamp
        const timestamp = parseInt(dateString);
        if (!isNaN(timestamp)) {
          return new Date(timestamp).toLocaleString();
        }

        return "Invalid date string";
      }

      // Handle numeric timestamp (milliseconds since epoch)
      if (typeof dateString === "number") {
        console.log("Processing numeric timestamp:", dateString);
        return new Date(dateString).toLocaleString();
      }

      // If all else fails, show raw value for debugging
      console.error("Could not process date:", dateString);
      return dateString ? dateString.toString() : "No date provided";
    } catch (error) {
      console.error("Error formatting date:", error, "Value:", dateString);
      return "Date error";
    }
  };

  // Filter bookings based on search term and filters
  const filteredBookings = bookings.filter((booking) => {
    // Filter by search text
    const resourceName = booking.resourceId?.name || "";
    const matchesSearch =
      resourceName.toLowerCase().includes(search) ||
      booking.purpose.toLowerCase().includes(search) ||
      (booking.department || "").toLowerCase().includes(search);

    // Filter by status
    const matchesStatus =
      filters.status === "all" || booking.status === filters.status;

    // Filter by department (REMOVED, always matches)
    // const matchesDepartment =
    //   filters.departmentFilter === "all" ||
    //   booking.department === filters.departmentFilter;

    // Filter by date range (if specified)
    const startDate = booking.startTime;
    let matchesDateRange = true;

    if (filters.dateRange.start && filters.dateRange.end) {
      const bookingDate = new Date(startDate);
      const start = new Date(filters.dateRange.start);
      const end = new Date(filters.dateRange.end);
      matchesDateRange = bookingDate >= start && bookingDate <= end;
    }

    return matchesSearch && matchesStatus && matchesDateRange;
  });

  // Render booking table
  const renderBookingTable = (
    bookingsToRender = filteredBookings,
    isPending = false
  ) => {
    // For Faculty role, My Bookings should only show own bookings
    // Sort bookingsToRender by createdAt descending (user's last booking first)
    const sortedBookings = [...bookingsToRender].sort(
      (a, b) => getCreatedAtTime(b.createdAt) - getCreatedAtTime(a.createdAt)
    );

    const tableTitle = isPending ? "Pending Approval Requests" : "My Bookings";

    if (
      (loading.bookings && !isPending) ||
      (loading.pendingBookings && isPending)
    ) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (bookingsToRender.length === 0) {
      return (
        <Paper sx={{ p: 3, textAlign: "center", mt: isPending ? 3 : 0 }}>
          <Typography variant="body1" color="text.secondary">
            {isPending
              ? "No pending booking requests found"
              : "No bookings found"}
          </Typography>
        </Paper>
      );
    }

    return (
      <Box sx={{ mt: isPending ? 3 : 0 }}>
        {isPending && (
          <Typography variant="h6" gutterBottom>
            {tableTitle}
          </Typography>
        )}
        <TableContainer component={Paper}>
          <Table size="medium">
            <TableHead>
              <TableRow>
                <TableCell>Created At</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Resource</TableCell>
                {isPending && <TableCell>Requested By</TableCell>}
                <TableCell>Purpose</TableCell>
                <TableCell>Time</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedBookings.map((booking) => (
                <TableRow key={booking._id}>
                  <TableCell>{formatDateTime(booking.createdAt)}</TableCell>
                  <TableCell>
                    <Chip
                      icon={
                        booking.resourceType === "lab_session" ? (
                          <RoomIcon />
                        ) : (
                          <ScienceIcon />
                        )
                      }
                      label={
                        booking.resourceType === "lab_session"
                          ? "Lab"
                          : "Equipment"
                      }
                      size="small"
                      color={
                        booking.resourceType === "lab_session"
                          ? "primary"
                          : "secondary"
                      }
                    />
                  </TableCell>
                  <TableCell>{booking.resourceId?.name || "Unknown"}</TableCell>
                  {isPending && (
                    <TableCell>
                      {booking.userId?.name || "Unknown User"}
                    </TableCell>
                  )}
                  <TableCell>{booking.purpose}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDateTime(booking.startTime)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      to {formatDateTime(booking.endTime)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={
                        booking.status.charAt(0).toUpperCase() +
                        booking.status.slice(1)
                      }
                      color={
                        booking.status === "approved"
                          ? "success"
                          : booking.status === "pending"
                          ? "warning"
                          : booking.status === "rejected"
                          ? "error"
                          : "default"
                      }
                    />
                  </TableCell>
                  <TableCell>
                    {isPending ? (
                      <Box>
                        <Tooltip title="Approve">
                          <IconButton
                            color="success"
                            size="small"
                            onClick={() => handleApproveBookingDialog(booking)}
                          >
                            <CheckCircleIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reject">
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => {
                              setSelectedBooking(booking);
                              setApprovalData({
                                status: "rejected",
                                notes: "",
                              });
                              setApproveDialogOpen(true);
                            }}
                          >
                            <CancelIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      <Tooltip title="Cancel Booking">
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleCancelBookingDialog(booking)}
                          disabled={
                            booking.status === "cancelled" ||
                            booking.status === "completed"
                          }
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // Render booking logbook table
  const renderBookingLogbook = () => {
    if (loadingLogs) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
          <CircularProgress />
        </Box>
      );
    }

    // Create filter controls for the logbook
    const logbookFilters = (
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Search"
              variant="outlined"
              size="small"
              value={search}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              placeholder="Search by resource, user, or purpose"
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Activity Type</InputLabel>
              <Select
                value={filters.activityType}
                onChange={(e) =>
                  handleFilterChange("activityType", e.target.value)
                }
                label="Activity Type"
              >
                <MenuItem value="all">All Activities</MenuItem>
                <MenuItem value="created">Created</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="modified">Modified</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<FilterListIcon />}
              onClick={fetchBookingLogs}
              fullWidth
            >
              Apply Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>
    );

    if (bookingLogs.length === 0) {
      return (
        <>
          {logbookFilters}
          <Paper sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="body1" color="text.secondary">
              No booking activity logs found
            </Typography>
          </Paper>
        </>
      );
    }

    // Sort logs by timestamp or createdAt descending (most recent first)
    const sortedLogs = [...bookingLogs].sort(
      (a, b) =>
        getCreatedAtTime(b.timestamp || b.createdAt) -
        getCreatedAtTime(a.timestamp || a.createdAt)
    );

    return (
      <>
        {logbookFilters}

        <TableContainer component={Paper}>
          <Table size="medium">
            <TableHead>
              <TableRow>
                <TableCell>Date & Time</TableCell>
                <TableCell>Activity</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Resource</TableCell>
                <TableCell>Booking Period</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedLogs.map((log) => {
                const activityType = log.activityType || "modified";

                // Determine the appropriate color for the activity chip
                let chipColor = "default";
                if (activityType === "created") chipColor = "info";
                else if (activityType === "approved") chipColor = "success";
                else if (
                  activityType === "rejected" ||
                  activityType === "cancelled"
                )
                  chipColor = "error";
                else if (activityType === "completed") chipColor = "secondary";
                else if (activityType === "modified") chipColor = "primary";

                return (
                  <TableRow key={log.id || log._id}>
                    <TableCell>
                      {formatDateTime(log.timestamp || log.createdAt)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          activityType.charAt(0).toUpperCase() +
                          activityType.slice(1)
                        }
                        size="small"
                        color={chipColor}
                      />
                    </TableCell>
                    <TableCell>
                      {getUserNameFromLog(log, users, userProfile) ||
                        "Unknown User"}
                      {log.user?.role && (
                        <Typography
                          variant="caption"
                          display="block"
                          color="text.secondary"
                        >
                          {log.user.role.charAt(0).toUpperCase() +
                            log.user.role.slice(1)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {getResourceNameFromLog(log, [...labs, ...equipment])}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {log.resourceType === "lab_session"
                          ? "Laboratory"
                          : "Equipment"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDateTime(
                          log.startTime || log.booking?.startTime
                        )}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        to {formatDateTime(log.endTime || log.booking?.endTime)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {log.purpose ||
                        log.booking?.purpose ||
                        log.notes ||
                        log.description ||
                        ""}
                      {log.notes && (
                        <Tooltip title={log.notes}>
                          <IconButton size="small">
                            <EventIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };

  // NewBookingForm component
  const NewBookingForm = ({
    labs,
    equipment,
    user,
    bookingType,
    setBookingType,
    onSubmit,
    loading,
  }) => {
    const [formData, setFormData] = useState({
      resourceType: bookingType,
      resourceId: "",
      startTime: dayjs().add(1, "hour").startOf("hour"),
      endTime: dayjs().add(2, "hour").startOf("hour"),
      purpose: "",
      notes: "",
    });

    // Update formData when bookingType changes
    React.useEffect(() => {
      setFormData((prev) => ({
        ...prev,
        resourceType: bookingType,
        resourceId: "",
      }));
    }, [bookingType]);

    const handleInputChange = (e) => {
      const { name, value } = e.target;
      setFormData({ ...formData, [name]: value });
    };
    const handleDateTimeChange = (name, value) => {
      setFormData({ ...formData, [name]: value });
    };
    const handleResourceSelect = (id) => {
      setFormData({ ...formData, resourceId: id });
    };
    const handleSubmit = (e) => {
      e.preventDefault();
      // Copy the local formData to the parent's formData before submitting
      const submissionData = { ...formData };
      onSubmit(submissionData);
    };

    // Button should be enabled only if all required fields are filled and valid
    const isSubmitDisabled =
      !formData.resourceId ||
      !formData.purpose ||
      !formData.startTime ||
      !formData.endTime ||
      (formData.endTime &&
        formData.startTime &&
        formData.endTime.isBefore(formData.startTime));

    // Only show true equipment (not consumables) when bookingType is 'equipment'
    let resources = [];
    if (bookingType === "lab_session") {
      resources = labs;
    } else if (bookingType === "equipment") {
      resources = equipment.filter(
        (item) =>
          item.isBookable &&
          item.category &&
          item.category.toLowerCase() !== "consumable" &&
          item.category.toLowerCase() !== "consumables"
      );
    }
    const isLoading =
      bookingType === "lab_session" ? loading.labs : loading.equipment;
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <form onSubmit={handleSubmit}>
          <Typography variant="h6" gutterBottom>
            Create New Booking
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Booking Type</InputLabel>
                <Select
                  value={bookingType}
                  onChange={(e) => setBookingType(e.target.value)}
                  label="Booking Type"
                >
                  <MenuItem value="lab_session">Laboratory Session</MenuItem>
                  <MenuItem value="equipment">Equipment</MenuItem>
                </Select>
                <FormHelperText>
                  Select whether you want to book a lab space or equipment
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Select{" "}
                {bookingType === "lab_session" ? "Laboratory" : "Equipment"}
              </Typography>
              <Box sx={{ mt: 2 }}>
                {isLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : resources.length === 0 ? (
                  <Typography
                    align="center"
                    color="text.secondary"
                    sx={{ py: 3 }}
                  >
                    No{" "}
                    {bookingType === "lab_session"
                      ? "laboratories"
                      : "equipment"}{" "}
                    available
                  </Typography>
                ) : (
                  <Grid container spacing={2}>
                    {resources.map((resource) => {
                      const resourceId = resource._id || resource.id;
                      return (
                        <Grid item xs={12} sm={6} md={4} key={resourceId}>
                          <Card
                            variant={
                              formData.resourceId === resourceId
                                ? "outlined"
                                : "elevation"
                            }
                            sx={{
                              cursor: "pointer",
                              borderColor:
                                formData.resourceId === resourceId
                                  ? "primary.main"
                                  : "transparent",
                              borderWidth:
                                formData.resourceId === resourceId ? 2 : 0,
                              boxShadow:
                                formData.resourceId === resourceId ? 3 : 1,
                            }}
                            onClick={() => handleResourceSelect(resourceId)}
                          >
                            <CardContent>
                              <Typography variant="h6" gutterBottom>
                                {resource.name}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                gutterBottom
                              >
                                {bookingType === "lab_session"
                                  ? `${resource.department} | Capacity: ${resource.capacity}`
                                  : `Category: ${
                                      resource.category
                                    } | Quantity: ${
                                      resource.quantity || "N/A"
                                    }`}
                              </Typography>
                              <Typography variant="body2">
                                {bookingType === "lab_session" &&
                                resource.location
                                  ? `${resource.location.building}, ${resource.location.floor}, Room ${resource.location.room}`
                                  : ""}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  label="Start Time"
                  value={formData.startTime}
                  onChange={(newValue) =>
                    handleDateTimeChange("startTime", newValue)
                  }
                  slotProps={{
                    textField: { fullWidth: true, margin: "normal" },
                  }}
                  minDate={dayjs()}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  label="End Time"
                  value={formData.endTime}
                  onChange={(newValue) =>
                    handleDateTimeChange("endTime", newValue)
                  }
                  slotProps={{
                    textField: { fullWidth: true, margin: "normal" },
                  }}
                  minDate={formData.startTime || dayjs()}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Purpose"
                name="purpose"
                value={formData.purpose}
                onChange={handleInputChange}
                required
                margin="normal"
                placeholder={`e.g., ${
                  bookingType === "lab_session"
                    ? "Database Lab Session"
                    : "Physics Experiment"
                }`}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Additional Notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                margin="normal"
                multiline
                rows={3}
                placeholder="Any special requirements or additional information"
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={isSubmitDisabled}
              startIcon={<CalendarTodayIcon />}
            >
              {userProfile.role === "admin" || userProfile.role === "faculty"
                ? "Create Booking"
                : "Request Booking"}
            </Button>
          </Box>
        </form>
      </Paper>
    );
  };

  // DEBUG: Log role status before rendering
  console.log(
    "BookingSystem Render - isAdmin:",
    isAdmin,
    "isFaculty:",
    isFaculty,
    "isScholar:",
    isScholar,
    "showNewBookingTab:",
    showNewBookingTab
  );

  // Show warning if admin is missing department (required for all admin features)
  const isAdminMissingDepartment = isAdmin && !department;

  return (
    <Layout title="Booking System">
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Booking System
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Book laboratory spaces and equipment for your classes and research
        </Typography>
        {/* Show warning if admin is missing department */}
        {isAdminMissingDepartment && (
          <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
            <b>Department Not Assigned:</b> Your admin profile is missing a
            department. Please contact a Developer Master to assign your
            department. Departmental features will not work until this is fixed.
          </Alert>
        )}
        {/* Show Create New Booking button for eligible users */}
        {showNewBookingTab && !isAdminMissingDepartment && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            sx={{ mt: 2, mb: 2 }}
            onClick={handleNewBooking}
            data-testid="create-new-booking-btn"
          >
            Create New Booking
          </Button>
        )}
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          aria-label="booking tabs"
        >
          {visibleTabs.map((tab, idx) => (
            <Tab
              key={tab.label}
              label={tab.label}
              disabled={isAdminMissingDepartment && tab.label !== "My Bookings"}
            />
          ))}
        </Tabs>
      </Paper>

      {/* My Bookings Tab */}
      <Box
        role="tabpanel"
        hidden={
          activeTab !== visibleTabs.findIndex((t) => t.label === "My Bookings")
        }
      >
        {activeTab ===
          visibleTabs.findIndex((t) => t.label === "My Bookings") && (
          <Box>{renderBookingTable()}</Box>
        )}
      </Box>

      {/* New Booking Tab - only for Faculty/Admin/Scholar */}
      {showNewBookingTab && (
        <Box
          role="tabpanel"
          hidden={
            activeTab !==
            visibleTabs.findIndex((t) => t.label === "New Booking")
          }
        >
          {activeTab ===
            visibleTabs.findIndex((t) => t.label === "New Booking") && (
            <NewBookingForm
              labs={labs}
              equipment={equipment}
              user={userProfile}
              bookingType={bookingType}
              setBookingType={setBookingType}
              onSubmit={handleSubmitBooking}
              loading={loading}
            />
          )}
        </Box>
      )}

      {/* Approve Bookings Tab - only for Admin/Faculty */}
      {(isAdmin || isFaculty) && (
        <Box
          role="tabpanel"
          hidden={
            activeTab !==
            visibleTabs.findIndex((t) => t.label === "Approve Bookings")
          }
        >
          {activeTab ===
            visibleTabs.findIndex((t) => t.label === "Approve Bookings") && (
            <Box>{renderBookingTable(pendingBookings, true)}</Box>
          )}
        </Box>
      )}

      {/* Booking Logbook Tab - for all users */}
      <Box
        role="tabpanel"
        hidden={
          activeTab !==
          visibleTabs.findIndex((t) => t.label === "Booking Logbook")
        }
      >
        {activeTab ===
          visibleTabs.findIndex((t) => t.label === "Booking Logbook") && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Booking Activity Logbook
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              This logbook provides a comprehensive history of all booking
              activities, including creations, approvals, rejections, and
              cancellations.
            </Typography>
            {renderBookingLogbook()}
          </Box>
        )}
      </Box>

      {/* Approval Dialog */}
      <Dialog
        open={approveDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {approvalData.status === "approved" ? "Approve" : "Reject"} Booking
          Request
        </DialogTitle>
        <DialogContent dividers>
          {selectedBooking && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Resource
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedBooking.resourceId?.name || "Unknown"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Requested By
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedBooking.userId?.name || "Unknown User"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Department
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedBooking.department}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Start Time
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {formatDateTime(selectedBooking.startTime)}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    End Time
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {formatDateTime(selectedBooking.endTime)}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Purpose
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedBooking.purpose}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <FormControl component="fieldset">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={approvalData.status === "approved"}
                          onChange={(e) =>
                            setApprovalData({
                              ...approvalData,
                              status: e.target.checked
                                ? "approved"
                                : "rejected",
                            })
                          }
                          color="success"
                        />
                      }
                      label={
                        approvalData.status === "approved"
                          ? "Approve"
                          : "Reject"
                      }
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes"
                    name="notes"
                    value={approvalData.notes}
                    onChange={handleApprovalInputChange}
                    margin="normal"
                    multiline
                    rows={3}
                    placeholder={
                      approvalData.status === "approved"
                        ? "Any additional information for the requestor"
                        : "Reason for rejection"
                    }
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmitApproval}
            variant="contained"
            color={approvalData.status === "approved" ? "success" : "error"}
          >
            {approvalData.status === "approved" ? "Approve" : "Reject"} Booking
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Booking Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Cancel Booking</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel this booking? This action cannot be
            undone.
          </Typography>
          {selectedBooking && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2">
                {selectedBooking.resourceType === "lab_session"
                  ? "Laboratory"
                  : "Equipment"}
                : {selectedBooking.resourceId?.name || "Unknown"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDateTime(selectedBooking.startTime)} to{" "}
                {formatDateTime(selectedBooking.endTime)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>
            Keep Booking
          </Button>
          <Button
            onClick={handleCancelBooking}
            variant="contained"
            color="error"
          >
            Cancel Booking
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
          action={
            lastAction && (
              <Button color="inherit" size="small" onClick={handleUndo}>
                UNDO
              </Button>
            )
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default BookingSystem;
